import { useMemo, useState } from 'react'

import { createPortal } from 'react-dom'

import styled from 'styled-components'

import { Job } from '../../../api/jobs'
import { ReviewQueueTracker } from '../../../api/reviewTasks'
import { blue } from '../../../styles/colors'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { MultiSelectOptionRow } from '../../common/MultiSelectOptionRow/MultiSelectOptionRow'
import {
  IViewSelectorOption,
  OptionPicker,
} from '../../common/ViewSelector/components/OptionPicker'
import { ViewSelector } from '../../common/ViewSelector/ViewSelector'

import {
  AiChangeBadge,
  ProgressAiSuggestion,
  ProgressAiSuggestionCard,
  isToOne,
  matchSuggestionsForJob,
  shortChangeLabel,
} from './ProgressAiSuggestionCard'
import {
  formatDurationHoursMinutes,
  isPendingReviewJob,
} from './reviewTaskFormat'
import {
  reviewRailBodyMaxHeightPx,
  TrackingCheckbox,
  TrackingDock,
  TrackingInstalledToggle,
  TrackingItemMain,
  TrackingItemRow,
  TrackingList,
  TrackingLoadingRow,
  TrackingProgress,
  TrackingProgressLabel,
  TrackingProgressRow,
  TrackingTypeBlock,
} from './styles'

const HoverCardPortal = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${p => p.$top}px;
  left: ${p => p.$left}px;
  z-index: 1200;
  width: 260px;
  max-width: calc(100vw - 24px);
  pointer-events: none;
  filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.16));
`

interface ReviewTrackingPanelProps {
  trackers: ReviewQueueTracker[]
  jobs: Job[]
  selectedJobIds: number[]
  viewed: number
  total: number
  sessionSeconds?: number
  selectedTrackerId: number | null
  onSelectedTrackerIdChange: (trackerId: number) => void
  visibleJobTypeIds: number[]
  showInstalled: boolean
  onShowInstalledChange: (show: boolean) => void
  onJobTypeVisibilityChange: (typeId: number, visible: boolean) => void
  onToggleJob: (jobId: number) => void
  onSelectJobs: (jobIds: number[], selected: boolean) => void
  onHoverItemId?: (itemId: number | null) => void
  hoveredItemId?: number | null
  loading?: boolean
  embedded?: boolean
  /** AI suggestions keyed by itemId::jobTypeName for the current viewpoint */
  aiSuggestionsByJobKey?: Record<string, ProgressAiSuggestion[]>
}

export const ReviewTrackingPanel = ({
  trackers,
  jobs,
  selectedJobIds,
  viewed,
  total,
  sessionSeconds = 0,
  selectedTrackerId,
  onSelectedTrackerIdChange,
  visibleJobTypeIds,
  showInstalled,
  onShowInstalledChange,
  onJobTypeVisibilityChange,
  onToggleJob,
  onSelectJobs,
  onHoverItemId,
  hoveredItemId = null,
  loading = false,
  embedded = true,
  aiSuggestionsByJobKey = {},
}: ReviewTrackingPanelProps) => {
  const [expanded, setExpanded] = useState(true)
  const [trackerExpanded, setTrackerExpanded] = useState(false)
  const [hoverCard, setHoverCard] = useState<{
    jobId: number
    suggestions: ProgressAiSuggestion[]
    top: number
    left: number
  } | null>(null)

  const trackerOptions: IViewSelectorOption[] = useMemo(
    () =>
      trackers.map(tracker => ({
        Id: String(tracker.id),
        Content: tracker.name,
        Options: [],
      })),
    [trackers],
  )

  const selectedTrackerOption =
    trackerOptions.find(option => option.Id === String(selectedTrackerId)) ||
    trackerOptions[0]

  const trackerJobs = useMemo(() => {
    if (selectedTrackerId == null) {
      return jobs
    }
    return jobs.filter(job => job.item.project_tracker === selectedTrackerId)
  }, [jobs, selectedTrackerId])

  const groups = useMemo(() => {
    const byType = new Map<
      number,
      { name: string; color: string; jobs: Job[] }
    >()
    trackerJobs.forEach(job => {
      const typeId = job.type.job_type.id
      const current = byType.get(typeId) || {
        name: job.type.job_type.name,
        color: job.type.job_type.display_color || blue,
        jobs: [],
      }
      current.jobs.push(job)
      byType.set(typeId, current)
    })
    return Array.from(byType.entries()).map(([id, group]) => ({
      id,
      ...group,
    }))
  }, [trackerJobs])

  const visibleTypeSet = useMemo(
    () => new Set(visibleJobTypeIds),
    [visibleJobTypeIds],
  )

  return (
    <TrackingDock $expanded={expanded} $embedded={embedded}>
      <ViewSelector
        title="Tracking"
        initialViewSelectorExpanded
        viewingExpanded={expanded}
        setViewingExpanded={setExpanded}
        maxHeight={expanded ? reviewRailBodyMaxHeightPx() : undefined}
        contentOverflow="scroll"
        fitContent={!expanded}
      >
        <TrackingProgress>
          <TrackingProgressLabel>Task Progress</TrackingProgressLabel>
          <TrackingProgressRow>
            <span>
              {viewed}/{total} ·{' '}
              {total ? Math.round((viewed / total) * 100) : 0}%
            </span>
            <span>Time: {formatDurationHoursMinutes(sessionSeconds)}</span>
          </TrackingProgressRow>
        </TrackingProgress>
        {trackers.length > 0 && (
          <OptionPicker
            expanded={trackerExpanded}
            setExpanded={setTrackerExpanded}
            viewingItems={{
              Id: 'root',
              Content: 'Tracker',
              Options: trackerOptions,
            }}
            selectedItem={selectedTrackerOption}
            onSelectOption={option => {
              onSelectedTrackerIdChange(Number(option.Id))
              setTrackerExpanded(false)
            }}
            showMarker
          />
        )}
        <TrackingInstalledToggle
          type="button"
          $active={showInstalled}
          onClick={() => onShowInstalledChange(!showInstalled)}
        >
          {showInstalled ? 'Hide installed' : 'Show installed'}
        </TrackingInstalledToggle>
        <TrackingList>
          {loading && (
            <TrackingLoadingRow>
              <LoadingIndicator fb size={16} />
              <span>Loading items…</span>
            </TrackingLoadingRow>
          )}
          {!loading && groups.length === 0 && (
            <div
              style={{ padding: '12px 8px', color: '#686868', fontSize: 13 }}
            >
              No remaining items at this viewpoint.
            </div>
          )}
          {!loading &&
            groups.map(group => {
              const groupIds = group.jobs.map(job => job.id)
              const selectedCount = groupIds.filter(id =>
                selectedJobIds.includes(id),
              ).length
              const typeVisible = visibleTypeSet.has(group.id)
              return (
                <TrackingTypeBlock key={group.id}>
                  <MultiSelectOptionRow
                    name={group.name}
                    displayColor={group.color}
                    checked={typeVisible}
                    showProgress={false}
                    onCheck={checked =>
                      onJobTypeVisibilityChange(group.id, checked)
                    }
                    selectAllChecked={
                      selectedCount === groupIds.length && groupIds.length > 0
                    }
                    selectAllIndeterminate={
                      selectedCount > 0 && selectedCount < groupIds.length
                    }
                    onSelectAll={checked => onSelectJobs(groupIds, checked)}
                  />
                  {typeVisible &&
                    group.jobs.map(job => {
                      const installed = !isPendingReviewJob(job)
                      const suggestions = matchSuggestionsForJob(
                        aiSuggestionsByJobKey,
                        job.item.id,
                        job.type.job_type.name,
                      )
                      const primary = suggestions[0]
                      return (
                        <TrackingItemRow
                          key={job.id}
                          $muted={installed}
                          $highlighted={hoveredItemId === job.item.id}
                          onMouseEnter={event => {
                            onHoverItemId?.(job.item.id)
                            if (suggestions.length === 0) {
                              setHoverCard(null)
                              return
                            }
                            const rect =
                              event.currentTarget.getBoundingClientRect()
                            const cardWidth = 260
                            const gap = 8
                            let left = rect.left - cardWidth - gap
                            if (left < 8) {
                              left = rect.right + gap
                            }
                            const estimatedHeight = Math.min(
                              180,
                              24 + suggestions.length * 110,
                            )
                            let top = rect.top
                            if (top + estimatedHeight > window.innerHeight - 8) {
                              top = Math.max(
                                8,
                                window.innerHeight - estimatedHeight - 8,
                              )
                            }
                            setHoverCard({
                              jobId: job.id,
                              suggestions,
                              top,
                              left,
                            })
                          }}
                          onMouseLeave={() => {
                            onHoverItemId?.(null)
                            setHoverCard(null)
                          }}
                        >
                          <TrackingItemMain>
                            <span>{job.item.id}</span>
                            {primary ? (
                              <AiChangeBadge $toOne={isToOne(primary.change)}>
                                {shortChangeLabel(primary.change)}
                              </AiChangeBadge>
                            ) : null}
                          </TrackingItemMain>
                          <TrackingCheckbox
                            type="checkbox"
                            checked={selectedJobIds.includes(job.id)}
                            onChange={() => onToggleJob(job.id)}
                          />
                        </TrackingItemRow>
                      )
                    })}
                </TrackingTypeBlock>
              )
            })}
        </TrackingList>
      </ViewSelector>
      {hoverCard &&
        hoverCard.suggestions.length > 0 &&
        createPortal(
          <HoverCardPortal $top={hoverCard.top} $left={hoverCard.left}>
            {hoverCard.suggestions.map(suggestion => (
              <ProgressAiSuggestionCard
                key={`${hoverCard.jobId}-${suggestion.item_id}-${suggestion.job_type}-${suggestion.change}-${suggestion.confidence}`}
                suggestion={suggestion}
              />
            ))}
          </HoverCardPortal>,
          document.body,
        )}
    </TrackingDock>
  )
}
